import { PUBLIC_BOTS_LOADING_ID } from "testIds";
import MyBotsPageProvider from "features/bots/MyBotsPageProvider";
import { openMessage } from "utils/misc.tsx";
import { BotTemplateData, BotType, LanguageNameSpaces } from "@shared/types";
import BotsPageWrapper from "features/bots/BotsPageWrapper.tsx";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import PublicBotCard from "features/bots/PublicBots/PublicBotCard.tsx";
import Button from "common/Buttons/Button.tsx";
import useQueryPublicBots from "features/bots/PublicBots/useQueryPublicBots.ts";
import usePublicBots from "@shared/features/bots/usePublicBots.ts";
import EmptyPublicBots from "features/bots/PublicBots/EmptyPublicBots.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import FlatList from "common/Lists/FlatList.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

const FormPublicBots = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { isDrawer } = useContext(DrawerContext) || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isFetching, filteredBots } = usePublicBots();
  const {
    publicBots,
    appliedPublicBot,
    unapplyConfiguration,
    showDivider,
    isBacktestingApplied,
  } = useQueryPublicBots({
    filteredBots,
  });

  const renderContent = ({
    item,
    index,
  }: {
    item: BotTemplateData;
    index: number;
  }) => {
    return (
      <PublicBotCard
        botData={item}
        key={index}
        id={`public-bot-${index + 1}`}
        botRank={index}
        isBotForm={true}
        divided={index > 0}
      />
    );
  };

  const renderTopContainer = () => {
    if (!appliedPublicBot || isBacktestingApplied) return null;
    if (theme.isNewLook) {
      // The bot whose configuration the form uses, marked, with the way
      // back; the other bots follow under a hairline.
      return (
        <NewLookApplied $divided={showDivider}>
          <PublicBotCard
            botData={appliedPublicBot}
            key={appliedPublicBot.id}
            id="applied-public-bot"
            isBotForm={true}
            isApplied={true}
          />
          <NewLookUnapply $gutter={isDrawer ? 16 : 20}>
            <Button
              $type="secondary"
              style={{ width: "100%" }}
              onClick={unapplyConfiguration}
            >
              {t("public_bots.unapply_configuration")}
            </Button>
          </NewLookUnapply>
        </NewLookApplied>
      );
    }
    return (
      <div>
        <PublicBotCard
          botData={appliedPublicBot}
          key={appliedPublicBot.id}
          id="applied-public-bot"
          isBotForm={true}
          isApplied={true}
        />
        <Button
          $type="secondary"
          style={{ width: "100%", marginTop: 16 }}
          onClick={unapplyConfiguration}
        >
          {t("public_bots.unapply_configuration")}
        </Button>
        {showDivider && <Divider />}
      </div>
    );
  };

  if (isFetching && !filteredBots.length && theme.isNewLook) {
    return (
      <NewLookLoading aria-busy="true" data-testid={PUBLIC_BOTS_LOADING_ID}>
        {[0, 1, 2, 3, 4].map((index) => (
          <NewLookSkeletonRow key={index} $divided={index > 0}>
            <Skeleton width={32} height={32} radius={16} />
            <NewLookSkeletonNames>
              <Skeleton width="48%" height={12} />
              <Skeleton width="64%" height={10} />
            </NewLookSkeletonNames>
            <Skeleton width={64} height={14} />
            <Skeleton width={84} height={30} radius={8} />
          </NewLookSkeletonRow>
        ))}
      </NewLookLoading>
    );
  }

  if (isFetching && !filteredBots.length) {
    return (
      <DataWrapper aria-busy="true" data-testid={PUBLIC_BOTS_LOADING_ID}>
        <LoadingSpinnerOverlay isLoading={true} spinnerMarginTop={80} />
      </DataWrapper>
    );
  }

  return (
    <MyBotsPageProvider
      openMessage={openMessage}
      botType={BotType.ASAP}
      isPublicBots={true}
    >
      <DataWrapper style={{ flexDirection: "column" }}>
        <BotsPageWrapper
          loading={isFetching}
          useFiltersChips={true}
          isSummaryTabs={true}
          padding={0}
          style={{
            position: "relative",
            margin: isDrawer
              ? isBottomNavigator
                ? "0 16px 12px"
                : "16px 16px 12px"
              : theme.isNewLook
              ? "16px 20px 0"
              : "12px 16px",
          }}
        />
        <FlatList
          data={publicBots || []}
          renderContent={renderContent}
          renderTopContainer={renderTopContainer}
          emptyState={
            !isFetching && <EmptyPublicBots inPanel={theme.isNewLook} />
          }
          listStyle={{ paddingBottom: 8 }}
          // New look: rows edge to edge, split by hairlines.
          gap={theme.isNewLook ? 0 : undefined}
          paddingHorizontal={theme.isNewLook ? 0 : undefined}
          loading={isFetching}
          loadingProps={{
            backgroundColor:
              theme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"],
            spinnerMarginTop: 200,
            spinnerSize: "default",
            // A refetch keeps the rows laid out under the overlay, so the
            // virtualized list keeps their measured heights.
            hideWithOpacity: true,
          }}
        />
      </DataWrapper>
    </MyBotsPageProvider>
  );
};

export default FormPublicBots;

const DataWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
  position: relative;
  overflow: visible;
  min-height: 200px;
  height: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    `
    container: public-bots-list / inline-size;
  `}
  /* New look: the list takes the height left under the filters (the empty
     content area above it no longer claims half of it); at 100% it
     overflowed the panel and its last card was cut off. */
  ${(p) =>
    p.theme.isNewLook &&
    `
    > :not(:last-child) {
      flex: none;
      height: auto;
    }
    > :last-child {
      flex: 1;
      min-height: 0;
      height: auto !important;
    }
  `}
`;
const NewLookApplied = styled.div<{ $divided?: boolean }>`
  padding-bottom: 12px;
  ${(p) =>
    p.$divided &&
    `
    border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
  `}
`;
const NewLookUnapply = styled.div<{ $gutter: number }>`
  padding: 12px ${(p) => p.$gutter}px 0;
`;
const NewLookLoading = styled.div`
  width: 100%;
  padding: 16px 0;
`;
const NewLookSkeletonRow = styled.div<{ $divided?: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 16px;
  min-height: 64px;
  padding: 12px 20px;
  ${(p) =>
    p.$divided &&
    `
    border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
  `}
`;
const NewLookSkeletonNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Divider = styled.div`
  border-bottom: ${(p) => p.theme.borders.light};
  margin-bottom: 12px;
  padding-top: 16px;
`;
